import { AlertTriangle, AlertCircle, Info, Activity } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  getAlarmSeverityLabel,
  getAlarmStateLabel,
  getReliabilityFriendlyLabel,
} from "@/lib/display-labels";

type Severity = "HIGH" | "MEDIUM" | "LOW";
type State = "UNACKNOWLEDGED" | "ACKNOWLEDGED" | "RESOLVED" | "SHELVED";
type Kind = "INFO" | "ALERT" | "ALARM";

const SEVERITY_COLOR: Record<Severity, string> = {
  HIGH: "bg-status-stopped/15 text-status-stopped",
  MEDIUM: "bg-status-idle/15 text-status-idle",
  LOW: "bg-muted text-muted-foreground",
};

const STATE_COLOR: Record<State, string> = {
  UNACKNOWLEDGED: "bg-destructive/15 text-destructive",
  ACKNOWLEDGED: "bg-warning/15 text-warning",
  RESOLVED: "bg-status-running/15 text-status-running",
  SHELVED: "bg-muted text-muted-foreground",
};

export function AlarmSeverityBadge({
  severity,
  className,
}: {
  severity: Severity;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
        SEVERITY_COLOR[severity],
        className
      )}
    >
      {getAlarmSeverityLabel(severity)}
    </span>
  );
}

export function AlarmStateBadge({ state, className }: { state: State; className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
        STATE_COLOR[state],
        className
      )}
    >
      {getAlarmStateLabel(state)}
    </span>
  );
}

// Confiabilidade suspeita (fonte instável / oscilando). Cor+dot+label
// (status nunca só por cor). Só renderiza quando SUSPECT — confiabilidade normal não polui.
export function AlarmReliabilityBadge({
  reliability,
  className,
}: {
  reliability: "HIGH" | "MEDIUM" | "LOW" | "SUSPECT";
  className?: string;
}) {
  if (reliability !== "SUSPECT") return null;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full bg-warning/15 px-2 py-0.5 text-xs font-medium text-warning",
        className
      )}
    >
      <Activity className="h-3 w-3" aria-hidden />
      Instável · confiabilidade {getReliabilityFriendlyLabel(reliability).toLowerCase()}
    </span>
  );
}

export function AlarmKindIcon({ kind, className }: { kind: Kind; className?: string }) {
  const base = cn("h-4 w-4", className);
  if (kind === "ALARM")
    return <AlertTriangle className={cn(base, "text-destructive")} aria-hidden />;
  if (kind === "ALERT") return <AlertCircle className={cn(base, "text-warning")} aria-hidden />;
  return <Info className={cn(base, "text-info")} aria-hidden />;
}
